import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface ContactFormData {
  name: string;
  email: string;
  company?: string;
  subject: string;
  message: string;
  inquiry_type?: string;
}

interface ContactFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess, onError }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<ContactFormData>({
    name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || '' : '',
    email: user?.email || '',
    company: '',
    subject: '',
    message: '',
    inquiry_type: 'support'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [referenceId, setReferenceId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const inquiryTypes = [
    { value: 'support', label: 'General Support' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing', label: 'Billing Question' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setErrorMessage('Please fill in all required fields.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/v1/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setReferenceId(result.reference_id || '');
        onSuccess?.();
        
        // Reset form
        setFormData({
          name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || '' : '',
          email: user?.email || '',
          company: '',
          subject: '',
          message: '',
          inquiry_type: 'support'
        });
      } else {
        throw new Error(result.detail || 'Failed to submit contact form');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to submit contact form. Please try again.';
      setErrorMessage(errorMsg);
      setSubmitStatus('error');
      onError?.(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 transform scale-110 origin-top">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl text-green-800 dark:text-green-200">Message Sent Successfully!</CardTitle>
          <CardDescription className="text-green-600 dark:text-green-300">
            Thank you for contacting us. We'll get back to you within 24 hours.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          {referenceId && (
            <div className="bg-green-100 dark:bg-green-800/30 p-3 rounded-lg mb-4">
              <p className="text-sm text-green-700 dark:text-green-300">
                <strong>Reference ID:</strong> {referenceId}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Please save this reference ID for your records
              </p>
            </div>
          )}
          <Button 
            onClick={() => setSubmitStatus('idle')}
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-800/30"
          >
            Send Another Message
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transform scale-110 origin-top">
      <CardHeader>
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-xl text-slate-800 dark:text-white text-center">Contact Support</CardTitle>
        <CardDescription className="text-slate-800 dark:text-gray-300 text-center">
          Send us a message and we'll get back to you within 24 hours
        </CardDescription>
      </CardHeader>
      <CardContent>
        {submitStatus === 'error' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700 dark:text-red-300 text-sm">{errorMessage}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-800 dark:text-gray-300 mb-2">
                Name *
              </label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Your full name"
                required
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-800 dark:text-gray-300 mb-2">
                Email *
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your.email@example.com"
                required
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-slate-800 dark:text-gray-300 mb-2">
                Company
              </label>
              <Input
                id="company"
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Your company name (optional)"
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              />
            </div>
            <div>
              <label htmlFor="inquiry_type" className="block text-sm font-medium text-slate-800 dark:text-gray-300 mb-2">
                Inquiry Type
              </label>
              <Select 
                value={formData.inquiry_type} 
                onValueChange={(value) => handleInputChange('inquiry_type', value)}
              >
                <SelectTrigger className="w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-md text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {inquiryTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-slate-800 dark:text-gray-300 mb-2">
              Subject *
            </label>
            <Input
              id="subject"
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Brief description of your inquiry"
              required
              className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-800 dark:text-gray-300 mb-2">
              Message *
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Please provide details about your inquiry..."
              required
              rows={6}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical contact-support-message-textarea"
              style={{ 
                color: '#111827 !important' // Force visible dark text in light mode
              }}
            />
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending Message...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Response time: Within 24 hours • info@quixocial.com
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactForm;